import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Space, Tooltip, Typography } from "antd";

import {
  selectAllUsersAndUserGroups,
  selectAssigneesAndUserGroups,
  selectPatientDetails,
  selectUser
} from "../../../store/selector";
import ErrorMessage from "../../customComponent/customMessages/ErrorMessage";
import SuccessMessage from "../../customComponent/customMessages/SuccessMessage";
import { updatePatientAssigneesAPI } from "../../../api/api";
import ACTIONS from "../../../store/action";
import { GRAY_400, TEXT4, WHITE } from "../../../constant/colors";
import { formatAssignee } from "../../../utils/formaters";
import { fontWeights, sizes } from "../../../constant/styles";
import AssigneesSelectField from "@tailormed/common-client/component/Select/AssigneesSelectField";

const { Text } = Typography;

const PatientAssignees = () => {
  const [isSelectHover, setIsSelectHover] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [patientAssignees, setPatientAssignees] = useState([]);
  const { patientId } = useParams();

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const patientDetails = useSelector(selectPatientDetails);
  const assignees = useSelector(selectAssigneesAndUserGroups);
  const allUsersAndUserGroups = useSelector(selectAllUsersAndUserGroups);
  const user = useSelector(selectUser);

  // Resolve through the wider users + groups list so a non-assignable existing
  // assignee is still rendered (and preserved on save with the `type` field the BE
  // requires) instead of being silently dropped. The dropdown options below stay
  // sourced from the assignable-only list.
  const resolveAssignee = (id) => allUsersAndUserGroups.find((a) => a.id?.toString() === id?.toString());

  useEffect(() => {
    if (!allUsersAndUserGroups.length || !patientDetails?.assignees?.length) return;

    const resolved = patientDetails.assignees
      .map(({ assignee }) => resolveAssignee(assignee))
      .filter(Boolean)
      .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));

    setPatientAssignees(resolved);
  }, [patientDetails, allUsersAndUserGroups]);

  const updateAssigneeHandler = async (values) => {
    try {
      setIsFetching(true);
      const newAssignees = values
        .map((value) => {
          const resolved = resolveAssignee(value);
          if (resolved) {
            // Clone instead of mutating the object returned by the memoized
            // selectAllUsersAndUserGroups selector (a live store reference).
            return { ...resolved, assignee: value };
          }
        })
        .filter(Boolean);

      const updateAssigneeRes = await updatePatientAssigneesAPI(patientId, newAssignees);

      if (updateAssigneeRes?.data?.success) {
        setPatientAssignees(newAssignees);
        SuccessMessage(t("successMessages.updated_assignees_successfully"));
        dispatch(ACTIONS.updatePatientDetailsAction({ ...patientDetails, assignees: newAssignees }));
      } else {
        ErrorMessage(t("errorMessages.failed_to_update_assignees"));
      }
    } catch (error) {
      ErrorMessage(t("errorMessages.failed_to_update_assignees"));
    } finally {
      setIsFetching(false);
    }
  };

  const assigneesTextToDisplay = patientAssignees?.map((assignee) => formatAssignee(assignee)).join(" | ") || "";

  return (
    <Space direction="vertical" size={0} style={{ width: "100%" }}>
      <Text style={{ fontSize: sizes.small, color: TEXT4, fontWeight: fontWeights.semibold, paddingLeft: 12 }}>
        {t("patientTopSection.assignees")}
      </Text>
      <Tooltip
        open={assigneesTextToDisplay.length && !isSelectOpen && isSelectHover}
        title={<Text>{assigneesTextToDisplay}</Text>}
        color={WHITE}
      >
        <AssigneesSelectField
          id="patient_header_assignees"
          meOptionId="patient_header_assignee_me"
          assignees={assignees}
          selectedAssignees={patientAssignees}
          currentUser={user}
          currentUserSuffix={t("bulk_actions.current_user_suffix")}
          value={patientAssignees.map(({ id }) => id)}
          onChange={updateAssigneeHandler}
          loading={isFetching}
          bordered={false}
          showArrow={isSelectHover || isFetching}
          maxTagTextLength={patientAssignees.length > 1 ? 8 : 16}
          placeholder={<Text style={{ color: GRAY_400, fontWeight: 400 }}>{t("no_assignees")}</Text>}
          onMouseEnter={() => setIsSelectHover(true)}
          onMouseLeave={() => setIsSelectHover(false)}
          onDropdownVisibleChange={(open) => setIsSelectOpen(open)}
          getPopupContainer={(trigger) => trigger.parentNode}
        />
      </Tooltip>
    </Space>
  );
};

export { PatientAssignees };
